import React, { useEffect, useRef, useState } from 'react';
import './ApiDocs.css';

declare global {
  interface Window {
    Redoc: any;
  }
}

const ApiDocs: React.FC = () => {
  const redocRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const redocInstanceRef = useRef<any>(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    let script: HTMLScriptElement | null = null;

    const initializeReDoc = () => {
      if (!window.Redoc || !redocRef.current) {
        setError('ReDoc failed to load properly');
        setIsLoading(false);
        return;
      }

      try {
        redocInstanceRef.current = window.Redoc.init(
          '/web/v1/docs/swagger.json',
          {
            scrollYOffset: 0,
            hideDownloadButton: false,
            expandSingleSchemaField: true,
            showExtensions: true,
            hideHostname: false,
            disableSearch: false,
            theme: {
              colors: {
                primary: {
                  main: '#3a4a63',
                },
                text: {
                  primary: '#333333',
                },
              },
              typography: {
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              },
            },
          },
          redocRef.current
        );
        setIsLoading(false);
        setError(null);
      } catch (error) {
        console.error('ReDoc initialization error:', error);
        setError('Failed to initialize API documentation');
        setIsLoading(false);
      }
    };

    // Load ReDoc script if not already loaded
    if (!window.Redoc) {
      script = document.createElement('script');
      script.src = 'https://cdn.redoc.ly/redoc/latest/bundles/redoc.standalone.js';
      script.async = true;
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        initializeReDoc();
      };

      script.onerror = () => {
        console.error('Failed to load ReDoc script');
        setError('Failed to load API documentation viewer');
        setIsLoading(false);
      };

      (document.head || document.getElementsByTagName('head')[0]).appendChild(script);
    } else {
      // ReDoc already loaded, initialize immediately
      initializeReDoc();
    }

    // Minimal cleanup - only destroy instance on unmount
    return () => {
      if (redocInstanceRef.current && typeof redocInstanceRef.current.destroy === 'function') {
        try {
          redocInstanceRef.current.destroy();
        } catch (error) {
          console.warn('Error destroying ReDoc instance:', error);
        }
      }
      redocInstanceRef.current = null;
    };
  }, []);

  return (
    <div className="api-docs-fullscreen" ref={containerRef}>
      {isLoading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading API Documentation...</p>
        </div>
      )}
      {error && (
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Unable to Load API Documentation</h3>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      )}
      <div
        ref={redocRef}
        className="redoc-container"
        style={{ display: error ? 'none' : 'block' }}
      ></div>
    </div>
  );
};

export default ApiDocs;